package com.mgacreative.hotel.feature.reservation.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import com.mgacreative.hotel.shared.ui.designsystem.components.verticalScrollbar
import org.koin.compose.viewmodel.koinViewModel
import com.mgacreative.hotel.shared.ui.designsystem.i18n.l10n

@Composable
fun CalendarScreen(
    viewModel: CalendarViewModel = koinViewModel(),
    onNavigate: (String) -> Unit = {}
) {
    val state by viewModel.state.collectAsState()
    val cellWidth = 100.dp
    val roomColWidth = 80.dp
    val rowHeight = 48.dp

    Scaffold(
        topBar = {
            Surface(tonalElevation = 2.dp, shadowElevation = 2.dp) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color.White)
                        .padding(horizontal = 4.dp, vertical = 2.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = { onNavigate("DashboardScreen") }) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Back"), tint = Color(0xFF1E3A8A))
                    }
                    Text(
                        text = l10n("Occupancy Calendar"),
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = Color(0xFF1E293B),
                        modifier = Modifier.weight(1f)
                    )
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        floatingActionButton = {
            Column(horizontalAlignment = Alignment.End) {
                FloatingActionButton(
                    onClick = { },
                    containerColor = Color(0xFF2196F3),
                    contentColor = Color.White,
                    modifier = Modifier.size(48.dp),
                    shape = CircleShape
                ) {
                    Icon(Icons.Default.DateRange, contentDescription = l10n("Calendar"))
                }
                Spacer(modifier = Modifier.height(16.dp))
                FloatingActionButton(
                    onClick = { onNavigate("CreateReservationScreen") },
                    containerColor = Color(0xFF2196F3),
                    contentColor = Color.White,
                    modifier = Modifier.size(56.dp),
                    shape = CircleShape
                ) {
                    Icon(Icons.Default.Add, contentDescription = l10n("Add"))
                }
            }
        },
        bottomBar = { CalendarBottomNavBar(currentRoute = "ReservationCalendarScreen", onNavigate = onNavigate) }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color.White)
        ) {
            val scrollState = rememberScrollState()
            val listState = androidx.compose.foundation.lazy.rememberLazyListState()

            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize().verticalScrollbar(listState)
            ) {
                item {
                    Row(modifier = Modifier.fillMaxWidth().height(rowHeight).border(1.dp, Color(0xFFF1F5F9))) {
                        Row(
                            modifier = Modifier.width(roomColWidth).fillMaxHeight(),
                            horizontalArrangement = Arrangement.Center,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(Icons.Default.LocationOn, contentDescription = "Location", tint = Color(0xFF2196F3), modifier = Modifier.size(16.dp))
                            Spacer(modifier = Modifier.width(8.dp))
                            Icon(Icons.Default.Search, contentDescription = "Search", tint = Color(0xFF2196F3), modifier = Modifier.size(20.dp))
                        }

                        Row(modifier = Modifier.fillMaxWidth().horizontalScroll(scrollState)) {
                            val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                            state.dates.forEach { date ->
                                val color = if (date == today) Color(0xFFEF4444) else Color.Gray
                                val dateStr = "${date.dayOfMonth.toString().padStart(2, '0')}/${date.monthNumber.toString().padStart(2, '0')}"
                                Box(
                                    modifier = Modifier.width(cellWidth).fillMaxHeight().border(0.5.dp, Color(0xFFF1F5F9)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(dateStr, color = color, fontSize = 14.sp)
                                }
                            }
                        }
                    }
                }

                if (state.isLoading) {
                    item {
                        Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                            CircularProgressIndicator()
                        }
                    }
                }

                items(state.rooms) { room ->
                    Row(modifier = Modifier.fillMaxWidth().height(rowHeight).border(0.5.dp, Color(0xFFF1F5F9))) {
                        Row(
                            modifier = Modifier.width(roomColWidth).fillMaxHeight().border(0.5.dp, Color(0xFFF1F5F9)).padding(horizontal = 8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            val dotColor = when (room.status) {
                                "CLEAN", "AVAILABLE" -> Color.Green
                                "OCCUPIED" -> Color.Red
                                "MAINTENANCE" -> Color.Gray
                                else -> Color.Gray
                            }
                            Box(modifier = Modifier.size(6.dp).clip(CircleShape).background(dotColor))
                            Spacer(modifier = Modifier.width(8.dp))
                            Text(room.roomNumber, color = Color(0xFF2196F3), fontSize = 14.sp)
                        }

                        Box(modifier = Modifier.fillMaxWidth().horizontalScroll(rememberScrollState())) {
                            Row {
                                for (i in 0 until state.dates.size) {
                                    Box(modifier = Modifier.width(cellWidth).fillMaxHeight().border(0.5.dp, Color(0xFFF1F5F9)))
                                }
                            }

                            val resList = state.reservations.filter { it.roomId == room.id }
                            resList.forEach { res ->
                                val checkInDate = try { LocalDate.parse(res.checkInDate) } catch (e: Exception) { null }
                                val checkOutDate = try { LocalDate.parse(res.checkOutDate) } catch (e: Exception) { null }

                                if (checkInDate != null && checkOutDate != null && state.dates.isNotEmpty()) {
                                    val startDate = state.dates.first()
                                    
                                    val startDaysDiff = checkInDate.toEpochDays() - startDate.toEpochDays()
                                    val durationDays = checkOutDate.toEpochDays() - checkInDate.toEpochDays()
                                    
                                    if (startDaysDiff + durationDays > 0 && startDaysDiff < state.dates.size) {
                                        val startCol = maxOf(0, startDaysDiff)
                                        val actualDuration = if (startDaysDiff < 0) durationDays + startDaysDiff else durationDays
                                        val span = minOf(actualDuration, state.dates.size - startCol)

                                        val color = when (res.status) {
                                            "CONFIRMED" -> Color(0xFF3B82F6)
                                            "PENDING" -> Color(0xFFFDBA74)
                                            "CHECKED_IN" -> Color(0xFF059669)
                                            else -> Color.Gray
                                        }

                                        val startOffset = startCol * cellWidth.value
                                        val blockWidth = span * cellWidth.value

                                        if (blockWidth > 0) {
                                            Box(
                                                modifier = Modifier
                                                    .offset(x = startOffset.dp, y = 6.dp)
                                                    .width(blockWidth.dp)
                                                    .height(36.dp)
                                                    .clip(RoundedCornerShape(6.dp))
                                                    .background(color)
                                                    .padding(horizontal = 8.dp),
                                                contentAlignment = Alignment.CenterStart
                                            ) {
                                                Row(verticalAlignment = Alignment.CenterVertically) {
                                                    Icon(Icons.Default.Person, contentDescription = null, tint = Color.White, modifier = Modifier.size(14.dp))
                                                    Spacer(modifier = Modifier.width(4.dp))
                                                    val guestName = res.guests?.firstName ?: l10n("Guest")
                                                    Text(guestName, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Medium)
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                
                item {
                    Row(modifier = Modifier.fillMaxWidth().height(rowHeight).border(0.5.dp, Color(0xFFF1F5F9))) {
                        Row(
                            modifier = Modifier.width(roomColWidth).fillMaxHeight().border(0.5.dp, Color(0xFFF1F5F9)).padding(horizontal = 8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(modifier = Modifier.size(6.dp).clip(CircleShape).background(Color.Gray))
                            Spacer(modifier = Modifier.width(8.dp))
                            Icon(Icons.Default.AddCircle, contentDescription = l10n("Add Room"), tint = Color(0xFF2196F3), modifier = Modifier.size(20.dp))
                        }
                        Box(modifier = Modifier.fillMaxWidth().background(Color(0xFFF8FAFC)))
                    }
                    Spacer(modifier = Modifier.height(100.dp))
                }
            }
        }
    }
}

@Composable
fun CalendarBottomNavBar(currentRoute: String, onNavigate: (String) -> Unit) {
    NavigationBar(
        containerColor = Color.White,
        contentColor = Color.Gray
    ) {
        NavigationBarItem(
            icon = { Icon(Icons.Default.Home, contentDescription = l10n("Front Office")) },
            label = { Text(l10n("Front Office"), fontSize = 10.sp) },
            selected = currentRoute == "NewReservationCalendarScreen",
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2196F3),
                selectedTextColor = Color(0xFF2196F3),
                indicatorColor = Color(0xFFE3F2FD)
            ),
            onClick = { onNavigate("NewReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.DateRange, contentDescription = l10n("Occupancy")) },
            label = { Text(l10n("Occupancy"), fontSize = 10.sp) },
            selected = currentRoute == "ReservationCalendarScreen",
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2196F3),
                selectedTextColor = Color(0xFF2196F3),
                indicatorColor = Color(0xFFE3F2FD)
            ),
            onClick = { onNavigate("ReservationCalendarScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.AutoMirrored.Filled.ArrowForward, contentDescription = l10n("Check-in")) },
            label = { Text(l10n("Check-in"), fontSize = 10.sp) },
            selected = currentRoute == "CheckInListScreen",
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2196F3),
                selectedTextColor = Color(0xFF2196F3),
                indicatorColor = Color(0xFFE3F2FD)
            ),
            onClick = { onNavigate("CheckInListScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.LocationOn, contentDescription = l10n("In Use")) },
            label = { Text(l10n("In Use"), fontSize = 10.sp) },
            selected = currentRoute == "InUseRoomsScreen",
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2196F3),
                selectedTextColor = Color(0xFF2196F3),
                indicatorColor = Color(0xFFE3F2FD)
            ),
            onClick = { onNavigate("InUseRoomsScreen") }
        )
        NavigationBarItem(
            icon = { Icon(Icons.AutoMirrored.Filled.List, contentDescription = l10n("List")) },
            label = { Text(l10n("List"), fontSize = 10.sp) },
            selected = currentRoute == "ReservationListScreen",
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2196F3),
                selectedTextColor = Color(0xFF2196F3),
                indicatorColor = Color(0xFFE3F2FD)
            ),
            onClick = { onNavigate("ReservationListScreen") }
        )
    }
}
